import { Injectable } from '@angular/core';
import { CommonConstant } from '../constants';

/**
 * One user's Guide progress. Versioned, so a later shape can migrate it.
 */
export interface HelpProgress {
    v: 1;
    /** Topic id -> taken or dismissed. A topic here is never offered on arrival again. */
    tours: { [topicId: string]: 'taken' | 'dismissed' };
    /** `<topicId>#<taskIndex>` -> the step indexes ticked. */
    steps: { [taskKey: string]: number[] };
    /** The walkthrough in progress, if any. */
    workflow: { id: string; step: number } | null;
}

/**
 * The signed-in user's Guide progress — tours taken or dismissed, steps
 * ticked, the walkthrough in progress — kept in this browser, keyed by
 * username so two people sharing a computer don't share progress.
 *
 * Each module is served from its own origin, and localStorage is per origin,
 * so this module's progress stays with this module — right for its screens'
 * tours and steps, which belong to it anyway. The tours of the frame every
 * module shares (the "Getting around Spectrum" topics) are also written to a
 * small cookie on the parent domain, which every module's subdomain reads, so
 * the welcome tour is offered once rather than once per module.
 */
@Injectable({ providedIn: 'root' })
export class GuideProgressService {
    private progress: HelpProgress = emptyProgress();
    private loadedKey: string | null = null;

    tourState(topicId: string): 'taken' | 'dismissed' | undefined {
        const local = this.read().tours[topicId];
        if (local === 'taken' || !isShared(topicId)) {
            return local;
        }
        return readShared(this.user)[topicId] || local;
    }

    markTour(topicId: string, state: 'taken' | 'dismissed'): void {
        const progress = this.read();
        if (progress.tours[topicId] !== 'taken') {
            progress.tours[topicId] = state;
            this.write();
        }
        if (isShared(topicId)) {
            writeShared(this.user, topicId, progress.tours[topicId]);
        }
    }

    ticked(topicId: string, task: number): number[] {
        return this.read().steps[`${topicId}#${task}`] || [];
    }

    toggleStep(topicId: string, task: number, step: number): void {
        const progress = this.read();
        const key = `${topicId}#${task}`;
        const current = new Set(progress.steps[key] || []);
        current.has(step) ? current.delete(step) : current.add(step);
        if (current.size) {
            progress.steps[key] = [...current].sort((a, b) => a - b);
        } else {
            delete progress.steps[key];
        }
        this.write();
    }

    resetTask(topicId: string, task: number): void {
        delete this.read().steps[`${topicId}#${task}`];
        this.write();
    }

    get workflow(): { id: string; step: number } | null {
        return this.read().workflow;
    }

    setWorkflow(workflow: { id: string; step: number } | null): void {
        this.read().workflow = workflow;
        this.write();
    }

    private get user(): string {
        try { return sessionStorage.getItem(CommonConstant.userName) || ''; } catch { return ''; }
    }

    private get key(): string {
        return `spectrum.guide.progress.${this.user}`;
    }

    /** Re-read when the user changes (a sign-out and sign-in in the same tab). */
    private read(): HelpProgress {
        const key = this.key;
        if (key !== this.loadedKey) {
            this.loadedKey = key;
            try {
                this.progress = normalise(JSON.parse(localStorage.getItem(key) || '{}'));
            } catch {
                this.progress = emptyProgress();
            }
        }
        return this.progress;
    }

    private write(): void {
        try { localStorage.setItem(this.key, JSON.stringify(this.progress)); } catch { /* storage full or blocked */ }
    }
}

// ---- the tours every module shares ------------------------------------------

/** The basics manual's topics: the frame, the same in every module. */
function isShared(topicId: string): boolean {
    return topicId.startsWith('spectrum.');
}

const SHARED_COOKIE = 'spectrum_guide_tours';
/** Enough for a few people sharing one computer; the oldest drop off. */
const SHARED_USERS = 6;

type SharedTours = { [userKey: string]: { [topicId: string]: 'taken' | 'dismissed' } };

/**
 * The user is keyed by a hash of the username, so the cookie — which rides
 * on every request to the school's domain — carries no email or mobile number.
 */
function userKey(user: string): string {
    let hash = 0x811c9dc5;
    for (let i = 0; i < user.length; i++) {
        hash ^= user.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(36);
}

function readAllShared(): SharedTours {
    try {
        const raw = document.cookie.split('; ').find(part => part.startsWith(SHARED_COOKIE + '='));
        const value = raw ? JSON.parse(decodeURIComponent(raw.slice(SHARED_COOKIE.length + 1))) : {};
        return value && typeof value === 'object' ? value : {};
    } catch {
        return {};
    }
}

function readShared(user: string): { [topicId: string]: 'taken' | 'dismissed' } {
    return readAllShared()[userKey(user)] || {};
}

function writeShared(user: string, topicId: string, state: 'taken' | 'dismissed'): void {
    const all = readAllShared();
    const key = userKey(user);
    const mine = { ...(all[key] || {}), [topicId]: state };
    delete all[key];
    // Most recent user last, so trimming drops whoever used the computer longest ago.
    const kept = Object.entries(all).slice(-(SHARED_USERS - 1));
    const value = encodeURIComponent(JSON.stringify({ ...Object.fromEntries(kept), [key]: mine }));
    const attributes = `; path=/; max-age=31536000; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
    try {
        // The widest domain this browser accepts: every module's subdomain reads
        // it. A public suffix (co.in) is refused, so each shorter candidate is
        // tried and read back; a bare host (localhost, an address) is host-only.
        for (const domain of parentDomains(location.hostname)) {
            document.cookie = `${SHARED_COOKIE}=${value}${attributes}; domain=${domain}`;
            if (readShared(user)[topicId] === state) {
                return;
            }
        }
        document.cookie = `${SHARED_COOKIE}=${value}${attributes}`;
    } catch { /* cookies blocked: this module's own copy still stands */ }
}

/**
 * `a.b.school.com` -> [`school.com`, `b.school.com`, `a.b.school.com`], widest
 * first; nothing for a bare host or an IP address. A domain cookie set on the
 * host itself still reaches its subdomains, so the host is a candidate too.
 */
function parentDomains(hostname: string): string[] {
    if (!hostname.includes('.') || /^[\d.]+$/.test(hostname) || hostname.includes(':')) {
        return [];
    }
    const labels = hostname.split('.');
    const domains: string[] = [];
    for (let take = 2; take <= labels.length; take++) {
        domains.push(labels.slice(-take).join('.'));
    }
    return domains;
}

function emptyProgress(): HelpProgress {
    return { v: 1, tours: {}, steps: {}, workflow: null };
}

function normalise(value: Partial<HelpProgress> | null | undefined): HelpProgress {
    const result = emptyProgress();
    if (value && typeof value === 'object') {
        if (value.tours && typeof value.tours === 'object') result.tours = { ...value.tours };
        if (value.steps && typeof value.steps === 'object') result.steps = { ...value.steps };
        if (value.workflow && typeof value.workflow.id === 'string') {
            result.workflow = { id: value.workflow.id, step: Number(value.workflow.step) || 0 };
        }
    }
    return result;
}
